import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { Package, Truck, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { Badge } from './components/ui/badge';
import { Card } from './components/ui/card';

export default function ReaderOrderHistory({ currentReader, authorId }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentReader || !authorId) {
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, 'authors', authorId, 'orders'),
      where('readerEmail', '==', currentReader.email)
    );
    const unsub = onSnapshot(q, (snap) => {
      const items = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setOrders(items);
      setLoading(false);
    }, (err) => {
      console.error('Order history listener error:', err);
      setLoading(false);
    });
    return unsub;
  }, [currentReader, authorId]);

  const getStatusBadge = (status) => {
    const badges = {
      Paid:       { color: 'bg-green-100 text-green-700',   label: 'Paid',       icon: CheckCircle },
      Shipped:    { color: 'bg-blue-100 text-blue-700',     label: 'Shipped',    icon: Truck },
      Delivered:  { color: 'bg-teal-100 text-teal-700',     label: 'Delivered',  icon: Package },
      Completed:  { color: 'bg-slate-100 text-slate-700',   label: 'Completed',  icon: CheckCircle },
      Cancelled:  { color: 'bg-red-100 text-red-700',       label: 'Cancelled',  icon: AlertCircle },
      // Legacy lowercase mappings
      pending:    { color: 'bg-green-100 text-green-700',   label: 'Paid',       icon: CheckCircle },
      payment:    { color: 'bg-green-100 text-green-700',   label: 'Paid',       icon: CheckCircle },
      paid:       { color: 'bg-green-100 text-green-700',   label: 'Paid',       icon: CheckCircle },
      processing: { color: 'bg-green-100 text-green-700',   label: 'Paid',       icon: CheckCircle },
      Pending:    { color: 'bg-green-100 text-green-700',   label: 'Paid',       icon: CheckCircle },
      Processing: { color: 'bg-green-100 text-green-700',   label: 'Paid',       icon: CheckCircle },
      shipped:    { color: 'bg-blue-100 text-blue-700',     label: 'Shipped',    icon: Truck },
      delivered:  { color: 'bg-teal-100 text-teal-700',     label: 'Delivered',  icon: Package },
      cancelled:  { color: 'bg-red-100 text-red-700',       label: 'Cancelled',  icon: AlertCircle },
    };
    const badge = badges[status] || badges.Paid;
    const Icon = badge.icon;
    return (
      <Badge className={badge.color}>
        <Icon className="h-3 w-3" />
        {badge.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Package className="mx-auto h-12 w-12 animate-pulse text-teal-600" />
          <p className="mt-2 text-slate-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-slate-900">Order History</h3>
        <Card className="rounded-2xl p-12 text-center">
          <Package className="mx-auto mb-3 h-12 w-12 text-slate-300" />
          <p className="font-medium text-slate-500">No orders yet</p>
          <p className="mt-1 text-sm text-slate-400">
            Orders you place will appear here with live status updates.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-slate-900">Order History</h3>
      <div className="space-y-3">
        {orders.map((order) => (
          <Card key={order.id} className="rounded-2xl p-5 shadow-sm">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="font-semibold text-slate-900">{order.productName}</p>
                <p className="text-xs text-slate-500">Order #{order.id.slice(-8).toUpperCase()}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {order.createdAt
                    ? new Date(order.createdAt).toLocaleDateString('en-GB', {
                        day: 'numeric', month: 'long', year: 'numeric'
                      })
                    : 'Date unavailable'}
                </p>
              </div>
              {getStatusBadge(order.status)}
            </div>

            <div className="rounded-lg bg-slate-50 p-3 text-sm">
              <div className="flex justify-between mb-1">
                <span className="text-slate-600">Product</span>
                <span className="font-semibold">£{(order.productPrice || 0).toFixed(2)}</span>
              </div>
              {order.shippingCost != null && (
                <div className="flex justify-between mb-1">
                  <span className="text-slate-600">Shipping</span>
                  <span className="font-semibold">£{(order.shippingCost || 0).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-slate-200 pt-2 mt-2">
                <span className="font-bold">Total</span>
                <span className="font-bold text-teal-600">£{(order.amount || order.total || 0).toFixed(2)}</span>
              </div>
            </div>

            {/* Tracking information */}
            {order.trackingNumber && (
              <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
                <div className="flex items-center gap-2 text-sm">
                  <Truck className="h-4 w-4 text-blue-600 shrink-0" />
                  <div className="min-w-0">
                    <p className="font-semibold text-blue-900">
                      {order.deliveryProvider ? `${order.deliveryProvider}: ` : 'Tracking: '}{order.trackingNumber}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
